/* Updated Sidebar: Added Quick Lists tab */
import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { useStore } from '../../store/StoreContext';

const navItems = [
  { path: '/', icon: 'PieChart', label: 'Dashboard' },
  { path: '/quick-lists', icon: 'List', label: 'Quick Lists' },
  { path: '/library/projects', icon: 'Folder', label: 'Active Projects' },
  { path: '/gantt', icon: 'AlignLeft', label: 'Gantt Chart' },
  { path: '/calendar', icon: 'Calendar', label: 'Calendar' },
  {
    label: 'Project Builder',
    icon: 'PenTool',
    path: '/builder',
    children: [
      { path: '/builder', label: 'New Project', icon: 'Plus' },
      { path: '/library/tasks', label: 'Task Templates', icon: 'CheckSquare' },
      { path: '/library/workflows', label: 'Workflow Templates', icon: 'GitMerge' },
    ]
  },
  { path: '/business-units', icon: 'Briefcase', label: 'Business Units' },
  { path: '/profile', icon: 'User', label: 'Account' },
  { path: '/users', icon: 'Users', label: 'Members' },
];

export const Sidebar = () => {
  const { state } = useStore();
  const location = useLocation();
  const [builderOpen, setBuilderOpen] = useState(location.pathname.includes('builder') || location.pathname.includes('library'));
  const role = state.currentUser?.role || 'member';
  const showUsersTab = ['master_admin', 'admin', 'admin_enterprise', 'admin_gold'].includes(role);
  const showBU = role !== 'member';
  
  const logoUrl = "https://media-manager-c.questera.ai/greta-media/981ecb6892631c31e92fca6d10d0332603596e5d429fd99cee63046a83be378b4365dffb7696bc6c52f453f1ea7f8977/images/aW1hZ2UvcG5n/5cfcf8e4487f6f866c526cb09ffe7a05.png";

  return (
    <div className="w-64 bg-base-800 border-r border-base-700 flex flex-col h-full">
      <div className="h-16 flex items-center px-6 border-b border-base-700">
        <NavLink to="/" className="flex items-center gap-3 group">
          <div className="w-9 h-9 rounded-xl overflow-hidden border border-primary/30 shadow-lg shadow-primary/10 group-hover:scale-110 transition-transform bg-white">
            <img src={logoUrl} className="w-full h-full object-contain p-1" alt="Logo" />
          </div>
          <span className="font-bold text-xl text-white tracking-tight group-hover:text-primary transition-colors">ToDo Toucan</span>
        </NavLink>
      </div>

      <nav className="flex-1 py-6 px-4 flex flex-col gap-1 overflow-y-auto custom-scrollbar">
        {navItems.map((item) => {
          if (item.path === '/users' && !showUsersTab) return null;
          if (item.path === '/business-units' && !showBU) return null;

          if (item.children) {
            const isActive = location.pathname.includes('builder') || location.pathname.includes('library');
            return (
              <div key={item.label} className="mb-1">
                <button
                  onClick={() => setBuilderOpen(!builderOpen)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'text-primary bg-primary/5' : 'text-gray-400 hover:text-white hover:bg-base-700'}`}
                >
                  <div className="flex items-center gap-3">
                    <SafeIcon name={item.icon} className="w-5 h-5" />
                    {item.label}
                  </div>
                  <SafeIcon name={builderOpen ? 'ChevronDown' : 'ChevronRight'} className="w-3 h-3" />
                </button>
                {builderOpen && (
                  <div className="mt-1 ml-4 pl-4 border-l border-base-700 flex flex-col gap-1">
                    {item.children.map(child => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        end={child.path === '/builder'}
                        className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors ${isActive ? 'bg-primary/10 text-primary' : 'text-gray-500 hover:text-white hover:bg-base-700'}`}
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-primary/10 text-primary' : 'text-gray-400 hover:text-white hover:bg-base-700'}`}
            >
              <SafeIcon name={item.icon} className="w-5 h-5" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-4 border-t border-base-700">
        <div className="bg-base-900/50 rounded-xl p-4 border border-base-700">
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-2">Workspace</div>
          <div className="text-xs font-semibold text-white truncate">{state.currentWorkspace?.name}</div>
          <div className="text-[9px] text-primary font-bold uppercase mt-1 opacity-70">Role: {role.replace('_', ' ')}</div>
        </div>
      </div>
    </div>
  );
};